<template>
    <div class="rank px-4 px-md-0 pt-3 pb-0">
        <!-- Headline -->
        <v-row no-gutters>
            <v-col cols="auto">
                <back-button />
            </v-col>
            <v-col cols="auto"
                   class="py-0 d-flex align-center">
                <h2 v-html="$options.filters.master($vuetify.lang.t('$vuetify.rank.title'))"></h2>
            </v-col>
        </v-row>

        <!-- Tab Bar -->
        <v-tabs @change="handleTabUpdate"
                class="mt-3"
                :value="filterAchievementTab"
                :show-arrows="false"
                background-color="transparent"
                height="40">
            <v-tab v-for="achievement in achievements"
                   :key="achievement.id"
                   class="px-2 font-p1 font-weight-bold">
                {{ achievement.brand.name }}
            </v-tab>
        </v-tabs>

        <!-- Tab Content -->
        <v-tabs-items @change="handleTabUpdate"
                      :value="filterAchievementTab"
                      class="mt-3 mb-2">
            <v-tab-item v-for="achievement in achievements"
                        :key="achievement.id"
                        background-color="transparent">
                <v-row>
                    <!-- aside (blue box) -->
                    <v-col cols="12"
                           md="4">
                        <pane variant="blue-gradient">
                            <div class="pt-6 pl-4 pr-4 pb-4 w-full text-center">
                                <v-img aspect-ratio="1"
                                       contain
                                       max-width="120px"
                                       width="100%"
                                       class="mb-4 mb-md-8 mx-auto"
                                       :src="achievement.actual_rank.icon"
                                       v-if="achievement.actual_rank.icon"></v-img>
                                <div v-html="$options.filters.master($vuetify.lang.t('$vuetify.rank.your_actual_rank'))"></div>
                                <div class="text-uppercase font-h1 d-inline-flex align-center">
                                        <span v-html="$options.filters.master(achievement.actual_rank.name[$vuetify.lang.current]
                                            ? achievement.actual_rank.name[$vuetify.lang.current]
                                            : achievement.actual_rank.name)"></span>
                                </div>

                                <!-- next possible rank -->
                                <div v-if="achievement.actual_rank.name === 'Member'" class="mt-4 text-center"
                                     v-html="$vuetify.lang.t('$vuetify.rank.rank_rise_1', $options.filters.master(achievement.next_rank.name))"></div>
                                <div v-else-if="achievement.actual_rank.name !== 'Member' && achievement.actual_rank.name !== 'Ultimate Master'" class="mt-4 text-center mb-3"
                                     v-html="$vuetify.lang.t('$vuetify.rank.rank_rise_2', $options.filters.master(achievement.next_rank.name))"></div>
                                <div v-else class="mt-4 text-center mb-3"
                                     v-html="$vuetify.lang.t('$vuetify.rank.rank_preservation', $options.filters.master(achievement.next_rank.name))"></div>

                                <div v-if="(achievement.actual_rank.name === 'Superior Master' || achievement.actual_rank.name === 'Legend Master') && currentYear === requiredYear">


                                    <v-dialog class="ml-auto d-inline"
                                              v-model="rank_protection"
                                              width="500">
                                        <template v-slot:activator="{ on, attrs }">
                                            <!-- protected -->
                                            <div v-bind="attrs"
                                                 v-on="on"
                                                 v-if="achievement.protection === true"
                                                 class="font-p2 text--grey d-flex align-center justify-center mt-4">
                                                <v-img
                                                    class="mr-1"
                                                    max-width="18"
                                                    max-height="18"
                                                    contain
                                                    src="@/assets/images/masterrank_preview/protection.svg"></v-img>
                                                <span>{{ $vuetify.lang.t('$vuetify.rank.rank_protection_false') }}</span>
                                                <v-icon size="14"
                                                        color="grey"
                                                        class="ml-1">mdi-information-outline
                                                </v-icon>
                                            </div>
                                            <!-- unprotected -->
                                            <div v-bind="attrs"
                                                 v-on="on"
                                                 v-else
                                                 class="font-p2 text--grey d-flex align-center justify-center mt-4">
                                                <v-img
                                                    class="mr-1"
                                                    max-width="18"
                                                    max-height="18"
                                                    contain
                                                    src="@/assets/images/masterrank_preview/protection-used.svg"></v-img>
                                                <span>{{ $vuetify.lang.t('$vuetify.rank.rank_protection_true') }}</span>
                                                <v-icon size="14"
                                                        color="grey"
                                                        class="ml-1">mdi-information-outline
                                                </v-icon>
                                            </div>
                                        </template>
                                        <pane variant="in-progress"
                                              class="font-p1">
                                            <pane variant="in-progress"
                                                  padding="modal-head">
                                                <div class="d-flex">
                                                    <span class="pt-3 pl-6 pb-3 font-weight-bold align-self-center text-uppercase">
                                                        {{ $vuetify.lang.t('$vuetify.rank.rank_protection') }}
                                                    </span>
                                                    <v-btn @click="rank_protection = false"
                                                           class="ml-auto mt-auto mb-auto mr-2"
                                                           icon
                                                           color="white">
                                                        <v-icon>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </pane>
                                            <pane class="pa-6"
                                                  variant="in-progress-darken"
                                                  v-html="$vuetify.lang.t('$vuetify.rank.rank_protection_desc')">
                                            </pane>
                                        </pane>
                                    </v-dialog>

                                </div>
                            </div>

                            <div class="mt-2 pl-10 pr-4 pb-4">
                                <level-system :lv-system="achievement.level_system" />
                            </div>
                        </pane>
                    </v-col>
                    <!-- content -->
                    <v-col cols="12"
                           md="8">

                        <!-- rank level + time frame -->
                        <v-row no-gutters class="mb-4">
                            <v-col>
                                <h2 class="font-h3 font-weight-bold">{{
                                        $vuetify.lang.t('$vuetify.rank.step', getCurrentStep(achievement.level_system))
                                                                     }}</h2>
                            </v-col>
                            <v-col class="text-right">
                                <span class="text--grey font-p1 font-weight-bold">
                                    {{ $vuetify.lang.t('$vuetify.rank.date_from_to', `01.01`, `31.12.${currentYear}`) }}
                                </span>
                            </v-col>
                        </v-row>

                        <!-- achievements headline + info button -->
                        <v-row no-gutters>
                            <div class="d-flex align-center full-width">
                                <h2 class="font-h2 font-weight-bold">
                                    {{ $vuetify.lang.t('$vuetify.rank.achievements') }}
                                </h2>
                                <v-dialog v-if="$vuetify.lang.t('$vuetify.rank.label_achievements') !== '' && $vuetify.lang.t('$vuetify.rank.label_achievements_desc') !== ''"
                                          class="ml-auto d-inline"
                                          v-model="achievements_dialog"
                                          width="500">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn v-bind="attrs"
                                               v-on="on"
                                               class="ml-auto"
                                               icon
                                               color="grey">
                                            <v-icon>mdi-information-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <pane variant="in-progress"
                                          class="font-p1">
                                        <pane variant="in-progress"
                                              padding="modal-head">
                                            <div class="d-flex">
                                                    <span class="pt-3 pl-6 pb-3 font-weight-bold align-self-center text-uppercase">
                                                        {{ $vuetify.lang.t('$vuetify.rank.label_achievements') }}
                                                    </span>
                                                <v-btn @click="achievements_dialog = false"
                                                       class="ml-auto mt-auto mb-auto mr-2"
                                                       icon
                                                       color="white">
                                                    <v-icon>mdi-close</v-icon>
                                                </v-btn>
                                            </div>
                                        </pane>
                                        <pane class="pa-6"
                                              variant="in-progress-darken"
                                              v-html="$vuetify.lang.t('$vuetify.rank.label_achievements_desc')">
                                        </pane>
                                    </pane>
                                </v-dialog>
                            </div>
                        </v-row>

                        <!-- achievements -->
                        <v-row>
                            <v-col cols="12"
                                   v-for="a in achievement.achievements"
                                   :key="a.id">
                                <pane class="pa-6">
                                    <v-row no-gutters>
                                        <v-col cols="auto"
                                               class="d-none d-md-block">
                                            <v-progress-circular class="mr-6"
                                                                 :rotate="90"
                                                                 :size="86"
                                                                 :width="3"
                                                                 :value="calcPercentage(a.progress, a.target)"
                                                                 color="primary">
                                                <v-avatar size="80">
                                                    <v-img :src="getMedalImage(a)"
                                                           :style="{ opacity: !a.completed ? .7 : 1 }"
                                                           aspect-ratio="1"
                                                           max-width="80px"></v-img>
                                                </v-avatar>
                                            </v-progress-circular>
                                        </v-col>
                                        <v-col>
                                            <v-row no-gutters>
                                                <v-col class="d-block d-md-none">
                                                    <v-flex class="d-flex align-center">
                                                        <v-progress-circular class="mr-6"
                                                                             :rotate="90"
                                                                             :size="56"
                                                                             :width="3"
                                                                             :value="calcPercentage(a.progress, a.target)"
                                                                             color="primary">
                                                            <v-avatar size="50">
                                                                <v-img :src="getMedalImage(a)"
                                                                       :style="{ opacity: !a.completed ? .7 : 1 }"
                                                                       aspect-ratio="1"
                                                                       max-width="50px"></v-img>
                                                            </v-avatar>
                                                        </v-progress-circular>
                                                        <span class="font-h2 font-weight-bold">{{
                                                                a.achievement.name
                                                                                               }}</span>
                                                    </v-flex>
                                                </v-col>
                                                <v-col cols="fill"
                                                       class="d-none d-md-block">
                                                    <span class="font-h2 font-weight-bold">{{
                                                            a.achievement.name
                                                                                           }}</span>
                                                </v-col>
                                                <v-col cols="auto"
                                                       class="d-flex align-center">
                                                    <span class="font-h2 font-weight-bold">{{ a.progress }}/{{
                                                            a.target
                                                                                           }}</span>
                                                </v-col>
                                            </v-row>
                                            <v-row no-gutters>
                                                <v-col>
                                                    <p class="mt-4 mb-0"
                                                       v-html="a.achievement.description"></p>
                                                </v-col>
                                            </v-row>
                                        </v-col>
                                    </v-row>
                                </pane>
                            </v-col>
                        </v-row>

                        <!-- masterpoints -->
                        <template v-if="achievement.actual_rank.name !== 'Member'">
                        <v-row no-gutters>
                            <div class="d-flex align-center full-width mt-4">
                                <h2 class="font-h2 font-weight-bold"
                                    v-html="$options.filters.master($vuetify.lang.t('$vuetify.rank.masterpoints'))"></h2>

                                <v-dialog v-if="$vuetify.lang.t('$vuetify.rank.label_masterpoints') !== '' && $vuetify.lang.t('$vuetify.rank.label_masterpoints_desc') !== ''"
                                          class="ml-auto d-inline"
                                          v-model="masterpoints_dialog"
                                          width="500">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn v-bind="attrs"
                                               v-on="on"
                                               class="ml-auto"
                                               icon
                                               color="grey">
                                            <v-icon>mdi-information-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <pane variant="in-progress"
                                          class="font-p1">
                                        <pane variant="in-progress"
                                              padding="modal-head">
                                            <div class="d-flex">
                                                    <span class="pt-3 pl-6 pb-3 font-weight-bold align-self-center text-uppercase">
                                                        {{ $vuetify.lang.t('$vuetify.rank.label_masterpoints') }}
                                                    </span>
                                                <v-btn @click="masterpoints_dialog = false"
                                                       class="ml-auto mt-auto mb-auto mr-2"
                                                       icon
                                                       color="white">
                                                    <v-icon>mdi-close</v-icon>
                                                </v-btn>
                                            </div>
                                        </pane>
                                        <pane class="pa-6"
                                              variant="in-progress-darken"
                                              v-html="$vuetify.lang.t('$vuetify.rank.label_masterpoints_desc')">
                                        </pane>
                                    </pane>
                                </v-dialog>

                            </div>
                        </v-row>

                        <v-row no-gutters>
                            <v-col cols="12">
                                <span class="text--grey font-p1 font-weight-bold"
                                      v-html="$options.filters.master($vuetify.lang.t('$vuetify.rank.required_masterpoints', achievement.masterpoints_target))"></span>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <pane class="pa-6 mb-8">
                                    <mp-multi-progress class="mb-2"
                                                       :percent="achievement.masterpoints_target"
                                                       :attribute-color-mapping="attributeColorMapping"
                                                       :items="achievement.masterpoints"></mp-multi-progress>

                                    <div class="d-flex justify-center">
                                        <i18n path="$vuetify.page_rang_detail.actual"
                                              class="d-flex align-center mb-6">
                                            <div class="d-flex align-baseline mx-1 error--text"
                                                 :class="{ 'success--text': achievement.masterpoints.sum_points >= achievement.masterpoints_target }">
                                                <span class="headline font-weight-bold">
                                                    {{ achievement.masterpoints.sum_points }}/
                                                </span>
                                                <span>{{ achievement.masterpoints_target }}</span>
                                            </div>
                                        </i18n>
                                    </div>

                                    <mp-progress v-for="(value, key) in achievement.masterpoints"
                                                 v-if="key !== 'sum_points'"
                                                 :key="key"
                                                 :name="key"
                                                 :user="currentUser"
                                                 :brand="brands[filterAchievementTab]"
                                                 :attribute-color-mapping="attributeColorMapping"
                                                 :value="value"
                                                 :ihk-certificate="achievement.ihk_certificate"
                                                 :max="50" />
                                </pane>
                            </v-col>
                        </v-row>
                        </template>
                        <v-row no-gutters>
                            <v-col cols="12">
                                <v-divider class="mb-4"/>
                            </v-col>
                            <template v-if="achievement.actual_rank.name !== 'Member'">
                                <v-col cols="auto">
                                    <v-icon class="mr-4">mdi-alert-circle-outline</v-icon>
                                </v-col>
                                <v-col cols="fill" class="mb-6">
                                    {{ $vuetify.lang.t('$vuetify.rank.conditions', `01.01.${currentYear + 1}`, getCurrentStep(achievement.level_system)) }}
                                </v-col>
                            </template>
                            <v-col cols="12">
                                <v-btn depressed
                                       color="primary darken-1"
                                       :to="urlRankInfo"
                                       v-if="$vuetify.lang.t('$vuetify.page_rang_detail.button_text')">
                                    <span v-html="$options.filters.master($vuetify.lang.t('$vuetify.page_rang_detail.button_text'))"></span>
                                </v-btn>
                            </v-col>
                        </v-row>

                    </v-col>
                </v-row>
            </v-tab-item>
        </v-tabs-items>

    </div>
</template>
<script>
import Achievement from '../models/Achievement';
import {mapActions, mapState} from 'vuex';
import BackButton from '../components/BackButton';
import HeadingSection from '../components/HeadingSection';
import Pane from '../components/Pane';
import {medalImage} from '../utils/helper';
import MpProgress from '../components/MpProgress';
import MpMultiProgress from '../components/MpMultiProgress';
import MpBar from '../components/MpBar';
import User from '../models/User';
import {ROUTE_STATIC_PAGE} from '../utils/constants';
import LevelSystem from '../components/LevelSystem';

export default {
    name: 'Rank',
    components: {
        LevelSystem,
        HeadingSection,
        BackButton,
        Pane,
        MpProgress,
        MpMultiProgress,
        MpBar,
    },
    data() {
        return {
            achievements_dialog: false,
            masterpoints_dialog: false,
            rank_protection: false,
            requiredYear: 2022,
            currentYear: new Date().getFullYear(),
        };
    },
    methods: {
        ...mapActions([
            'setFilter',
        ]),
        handleTabUpdate(e) {
            this.setFilter({
                item: 'filterAchievementTab',
                value: e,
            });
        },
        calcPercentage(current, max) {
            const percentage = current / max * 100;
            return percentage >= 100 ? 100 : percentage;
        },
        getMedalImage(achievementItem) {
            const item = achievementItem.achievement.identifier ? achievementItem.achievement.identifier : achievementItem.achievement.name.toLowerCase();
            return medalImage(item, achievementItem.completed);
        },
        getCurrentStep(levelSystem) {
            let currStep = 1;
            if (levelSystem.steps && levelSystem.steps.length > 1) {
                if (levelSystem.steps[0].state === 'succeeded') {
                    currStep = 2;
                }
            }
            return currStep;
        },
    },
    computed: {
        ...mapState([
            'filterAchievementTab',
        ]),
        achievements() {
            return Achievement.getAll();
        },
        attributeColorMapping() {
            return {
                trade_fairs: '#be0443',
                trainings: '#0089bf',
                sold_vehicles: '#004a79',
                ihk_premium_seller: '#e5b900',
                google_rating: '#2fc156',
            };
        },
        brands() {
            return this.achievements.map((achievement, index) => {
                return {
                    id: index,
                    name: achievement.brand.name.toUpperCase(),
                };
            });
        },
        currentUser() {
            return User.getCurrent();
        },
        maximumMasterpoints() {
            if (new Date().getFullYear() === 2021) {
                return 80;
            }
            return 100;
        },
        urlRankInfo() {
            return {name: ROUTE_STATIC_PAGE, params: {slug: 'rank'}};
        },
    },
};
</script>